//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Asset Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'
import ArrayStore from 'devextreme/data/array_store'

//#endregion

import { Enums } from '../../../../models/common'
import { OpConsumer, OpConsumerPaymentModule, OpPaymentModule } from '../../../../models/core'

import { LocalizationHelper, TypeHelper } from '../../../../helpers'
import { ConsumerHelper } from '../../consumers.helper'
import { UserSessionService } from '../../../../services/user-session'

import { GridOptions, GridController, GridTypes } from '../../../common/grid/grid.controller'
import { ConsumerDetailsPaymentsOptions, ConsumerDetailsPayments } from "./consumers-details-payments.interfaces"


export class ConsumerDetailsPaymentsController implements ng.IComponentController, ConsumerDetailsPayments {
    // #region Consts

    private static get PAYMENTS_GRID_NAME(): string { return 'consumerDetailsPaymentsGrid' }
    private static get PAYMENT_GRID_ROW_WIDTH(): number { return 10 }
    private static get PAYMENT_GRID_ROW_HEIGHT(): number { return 30 }

    // #endregion


    // #region Members

    private options: ConsumerDetailsPaymentsOptions

    private paymentsGridOptions: GridOptions
    private paymentsGridDataSource: DataSource
    public paymentsGrid: GridController<OpPaymentModule>
    private paymentsBeforeChanges: OpConsumerPaymentModule[] = []

    private isLoading: boolean
    private idConsumer: number

    private consumer: OpConsumer
    private consumerBeforeChanges: OpConsumer
    public toolbarOptions: DevExpress.ui.dxToolbarOptions

    // #endregion


    // #region Ctor

    public constructor(private userSession: UserSessionService) { }

    // #endregion


    // #region IComponentController Implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    private initialize(): void {
        this.isLoading = true

        if (this.options != null) {
            const deferred: JQueryDeferred<void> = $.Deferred<void>()
            this.consumer = this.options.consumer

            if (this.consumer == null) {
                this.getConsumer(this.options.idConsumer)
                    .then((consumer: OpConsumer) => {
                        this.consumer = consumer
                        this.options.consumer = consumer

                        deferred.resolve()
                    })
                    .fail(() => {
                        deferred.reject()
                    })
            } else {
                deferred.resolve()
            }

            deferred.promise()
                .then(() => {
                    this.consumerBeforeChanges = this.consumer.clone(true, true, true, true)
                    this.setDataSource(this.consumer.paymentModules)
                })
                .fail(() => {
                    this.paymentsGridDataSource = null
                })
                .always(() => {
                    this.setToolbarOptions()
                    this.setPaymentsGridOptions().then(() => {
                        this.isLoading = false
                    })
                })
        } else {
            this.isLoading = false
        }
    }

    private refresh(): void {
        this.consumerBeforeChanges = null
        this.consumer = null

        this.paymentsGrid = null
        this.paymentsGridDataSource = null
        this.paymentsGridOptions = null

        this.initialize()
    }

    public toggleLoading(loading: boolean): void {
        this.isLoading = loading
    }

    // #region Payments Grid

    private setDataSource(data: OpConsumerPaymentModule[]): void {
        this.paymentsGridDataSource = new DataSource({
            store: new ArrayStore({
                key: 'idPaymentModule',
                data: data
            })
        })
    }

    private getConsumer(idConsumer: number): JQueryPromise<OpConsumer> {
        return this.userSession.getDataProvider().Consumer.getConsumer(idConsumer).then((consumer: OpConsumer) => {
            return consumer
        })
    }

    private getPaymentsGridColumns(): DevExpress.ui.dxDataGridColumn[] {
        const activity: DevExpress.ui.dxDataGridColumn = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Active),
            allowEditing: false,
            dataType: 'boolean',
            calculateCellValue: (rowData: OpConsumerPaymentModule): any => {
                return this.isPaymentSystemNumberValid(rowData.paymentSystemNumber)
            }
        }

        const paymentSystemNumber: DevExpress.ui.dxDataGridColumn = {
            dataField: 'paymentSystemNumber',
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PaymentModuleNumber),
            width: ConsumerDetailsPaymentsController.PAYMENT_GRID_ROW_WIDTH,
            allowEditing: true
        }

        const paymentModuleName: DevExpress.ui.dxDataGridColumn = {
            dataField: 'paymentModule',
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PaymentSystem),
            allowEditing: false
        }

        const actions: DevExpress.ui.dxDataGridColumn = {
            dataField: '',
            caption: '',
            allowEditing: false,

            cellTemplate: (container, options) => {

                let rowIndex: number = options.rowIndex

                let addPaymentSystemNumberButtonOptions: DevExpress.ui.dxButtonOptions = {
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AddCID),
                    onClick: (e: any) => {
                        this.addPaymentSystemNumber(rowIndex)
                    }
                }

                let clearPaymentSystemNumberButtonOptions: DevExpress.ui.dxButtonOptions = {
                    icon: 'close',
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Clear),
                    onClick: (e: any) => {
                        this.clearPaymentSystemNumber(rowIndex)
                    },
                }

                let div: JQuery = $('<div class="consumers-details-payments-grid-buttons__buttons-container"></div>')

                div.appendTo(container)

                let addPaymentSystemNumberButton: JQuery = $('<div class="consumers-details-payments-grid-buttons__button">CID</div>')

                addPaymentSystemNumberButton.dxButton(addPaymentSystemNumberButtonOptions)
                addPaymentSystemNumberButton.appendTo(div)

                let clearPaymentSystemNumberButton: JQuery = $('<div class="consumers-details-payments-grid-buttons__button"></div>')

                clearPaymentSystemNumberButton.dxButton(clearPaymentSystemNumberButtonOptions)
                clearPaymentSystemNumberButton.appendTo(div)
            }
        }

        return [activity, paymentSystemNumber, paymentModuleName, actions]
    }

    private addPaymentSystemNumber(rowIndex: number): void {
        let CID: string = TypeHelper.emptyString

        if (this.consumer.currentLocation != null && this.consumer.currentLocation.dataList != null) {
            CID = this.consumer.currentLocation.dataList.getValue(Enums.DataType.LOCATION_CID)
        }

        this.consumer.paymentModules[rowIndex].paymentSystemNumber = CID

        this.setDataSource(this.consumer.paymentModules)
    }

    private clearPaymentSystemNumber(rowIndex: number): void {
        this.consumer.paymentModules[rowIndex].paymentSystemNumber = TypeHelper.emptyString

        this.setDataSource(this.consumer.paymentModules)
    }

    private setPaymentsGridOptions(): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()

        if (this.paymentsGridOptions == null || this.paymentsGrid == null) {
            this.paymentsGridOptions = {
                height: '100%',
                addCustomFilters: true,
                buttonsOptions: {
                    enabled: true,
                },
                columns: this.getPaymentsGridColumns(),
                name: ConsumerDetailsPaymentsController.PAYMENTS_GRID_NAME,
                type: GridTypes.MAIN,
                dataSource: this.paymentsGridDataSource,
                editing: {
                    editEnabled: true,
                    mode: "cell",
                },
                onInitialized: (e: any, gridController: GridController<OpPaymentModule>) => {
                    this.paymentsGrid = gridController

                    if (_.isFunction(this.options.onInitialized)) {
                        this.options.onInitialized(this)
                    }

                    deferred.resolve()
                },
                onRowPrepared: (e: any) => {
                    e.rowElement.css({ height: ConsumerDetailsPaymentsController.PAYMENT_GRID_ROW_HEIGHT });
                }
            }
        } else {
            deferred.resolve()
        }

        return deferred.promise()
    }

    private setToolbarOptions(): void {
        this.toolbarOptions = {
            items: [{
                location: 'after',
                widget: 'dxButton',
                options: {
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),
                    onClick: (e: any) => {
                        const cellFocusOutTimeout: number = 500
                        setTimeout(() => {
                            this.saveConsumer()
                        }, cellFocusOutTimeout)
                    }
                }
            }]
        }
    }

    // #endregion


    private isPaymentSystemNumberValid(paymentSystemNumber: string): boolean {
        if (paymentSystemNumber == null) {
            return false
        }

        if (paymentSystemNumber.trim() == '') {
            return false
        }

        return true
    }

    private setOpStates(): void {

        let isGridItems: boolean
        let gridItems: OpConsumerPaymentModule[]

        if (this.paymentsGrid && this.paymentsGrid.dxGrid.getDataSource().items() != null) {
            isGridItems = true
            gridItems = this.paymentsGrid.dxGrid.getDataSource().items()
        } else {
            isGridItems = false
        }

        if (this.consumerBeforeChanges != null && this.consumerBeforeChanges.paymentModules != null && isGridItems) {
            const beforeChanges: OpConsumerPaymentModule[] = _.sortBy(this.consumerBeforeChanges.paymentModules, (element: OpConsumerPaymentModule) => element.idPaymentModule)
            const afterChanges: OpConsumerPaymentModule[] = _.sortBy(gridItems, (gridItem: OpConsumerPaymentModule) => gridItem.idPaymentModule)

            for (let i = 0; i < beforeChanges.length; i++) {
                const beforeChangesElement: OpConsumerPaymentModule = beforeChanges[i]
                const afterChangesElement: OpConsumerPaymentModule = afterChanges[i]

                if (!this.isPaymentSystemNumberValid(afterChangesElement.paymentSystemNumber)) {
                    afterChangesElement.paymentSystemNumber = null
                }

                if (beforeChangesElement.paymentSystemNumber === null && afterChangesElement.paymentSystemNumber !== null) {
                    afterChangesElement.opState = Enums.OpChangeState.New
                } else if (beforeChangesElement.paymentSystemNumber !== null && afterChangesElement.paymentSystemNumber === null) {
                    afterChangesElement.opState = Enums.OpChangeState.Delete
                } else if (beforeChangesElement.paymentSystemNumber !== afterChangesElement.paymentSystemNumber) {
                    afterChangesElement.opState = Enums.OpChangeState.Modified
                } else if (beforeChangesElement.paymentSystemNumber === afterChangesElement.paymentSystemNumber) {
                    afterChangesElement.opState = Enums.OpChangeState.Loaded
                }
            }

            this.consumer.paymentModules = afterChanges
        }
    }

    private saveConsumer(): void {
        this.isLoading = true
        this.setOpStates()

        // Avatar jest nullowany, ponieważ powoduje to problem przy deserializacji.
        // Z uwagi, że backend i tak nie korzysta wartości z tego pola tylko z DataList, wartość została wynullowana.
        if (this.consumer.actor != null && this.consumer.actor.avatar != null) {
            this.consumer.actor.avatar = null
        }

        ConsumerHelper
            .saveConsumer(this.userSession.getDataProvider(), this.consumer, Enums.SimaxModuleName.Consumer)
            .then((consumer: OpConsumer) => {
                this.options.consumer = consumer
            })
            .always(() => {
                setTimeout(() => {
                    this.isLoading = false
                }, 2000)
                this.refresh()
            })
    }
}